import { reportUsage } from '@ms/yammer-data/dist/telemetry';
import { lazyLoadOnRender } from '@ms/yammer-libs-lazy';
import { combinePath } from '@ms/yammer-web-support/dist/location';
import React, { FC, ReactElement, memo, useEffect, useLayoutEffect, useMemo } from 'react';
import { Redirect, Route, Switch, useLocation, useRouteMatch } from 'react-router-dom';

import { getHeroStringForTelemetry } from '../../telemetry/hero';
import { LoadableDogfoodPerfHUD } from '../DogfoodPerfHUD';
import PageDataLoadContextProvider from '../PageDataLoadContextProvider';
import PageRouteAssetPreloadProvider from '../PageRouteAssetPreloadProvider';
import { usePageTelemetryContextSetRouteCallback } from '../PageTelemetryContextProvider/hooks';

import {
  AppPageRoutesWithTelemetryProps,
  PageRoute,
  ParentPageRoute,
  parentRouteHasRedirectRoute,
} from './AppPageRoutesWithTelemetry.types';
import SubrouteComponentPreloader from './SubrouteComponentPreloader';
import { enforceNoDuplicatePageNames } from './enforceNoDuplicatePageNames';
import { isParentRoute } from './isParentRoute';

interface LoadablePageContentWithTelemetryProps {
  readonly route: PageRoute;
}
const LoadablePageContentWithTelemetry: FC<LoadablePageContentWithTelemetryProps> = ({ route }) => {
  const LoadablePageContent = useMemo(
    () =>
      lazyLoadOnRender({
        loader: route.loader,
      }),
    [route.loader]
  );
  const { pathname } = useLocation();

  const setRoute = usePageTelemetryContextSetRouteCallback();

  useLayoutEffect(() => {
    setRoute?.(route, pathname);
  }, [pathname, route, setRoute]);

  useEffect(() => {
    const pageVisitEventName = route.page === 'Precache' ? 'page_visit_precache' : 'page_visit';
    reportUsage({
      eventName: pageVisitEventName,
      eventProperties: {
        page: route.page,
        hero: getHeroStringForTelemetry(route.hero),
        path: route.path,
        urlPath: pathname,
      },
    });
  }, [pathname, route.hero, route.page, route.path]);

  return route.dataLoader ? (
    <PageDataLoadContextProvider key={pathname} dataLoader={route.dataLoader}>
      <LoadablePageContent />
    </PageDataLoadContextProvider>
  ) : (
    <LoadablePageContent />
  );
};

type GetRouteElement = (route: PageRoute) => ReactElement;
const getRouteElement: GetRouteElement = (route) => (
  <Route
    key={route.path}
    path={route.path}
    exact={route.exact}
    render={() => <LoadablePageContentWithTelemetry route={route} />}
  />
);

type GetFallbackRedirectElement = (to: string) => ReactElement;
const getFallbackRedirectElement: GetFallbackRedirectElement = (to) => (
  <Route key={to} path="*" render={() => <Redirect to={to} />} />
);

interface LoadableParentContentProps {
  readonly parentRoute: ParentPageRoute;
}
const LoadableParentContent: FC<LoadableParentContentProps> = memo(({ parentRoute }) => {
  const LoadableParentPageContent = useMemo(
    () =>
      lazyLoadOnRender({
        loader: parentRoute.loader,
        fallback: <SubrouteComponentPreloader parentRoute={parentRoute} />,
      }),
    [parentRoute]
  );

  const { pathname } = useLocation();
  const match = useRouteMatch({
    path: parentRoute.path,
  });
  const parentRoutePath = match ? match.url : pathname;
  const routeElements = parentRoute.childRoutes.map((route) =>
    getRouteElement({ ...route, path: combinePath(parentRoute.path, route.path) })
  );

  if (parentRouteHasRedirectRoute(parentRoute)) {
    routeElements.push(getRouteElement({ ...parentRoute.fallbackRedirectRoute, path: parentRoute.path }));
  } else {
    routeElements.push(getFallbackRedirectElement(parentRoute.fallbackRedirectPath));
  }

  return parentRoute.dataLoader ? (
    <PageDataLoadContextProvider key={parentRoutePath} dataLoader={parentRoute.dataLoader}>
      <LoadableParentPageContent>
        <Switch>{routeElements}</Switch>
      </LoadableParentPageContent>
    </PageDataLoadContextProvider>
  ) : (
    <LoadableParentPageContent>
      <Switch>{routeElements}</Switch>
    </LoadableParentPageContent>
  );
});

type GetParentRouteElement = (parentRoute: ParentPageRoute) => ReactElement;
const getParentRouteElement: GetParentRouteElement = (parentRoute) => (
  <Route
    key={parentRoute.path}
    path={parentRoute.path}
    render={() => <LoadableParentContent parentRoute={parentRoute} />}
  />
);

const AppPageRoutesWithTelemetry: FC<AppPageRoutesWithTelemetryProps> = ({ routes, disablePerfHUD }) => {
  enforceNoDuplicatePageNames(routes);

  const pageRoutes = routes.map((route) =>
    isParentRoute(route) ? getParentRouteElement(route) : getRouteElement(route)
  );

  const perfHUD = disablePerfHUD ? null : <LoadableDogfoodPerfHUD />;

  return (
    <PageRouteAssetPreloadProvider routes={routes}>
      <Switch>{pageRoutes}</Switch>
      {perfHUD}
    </PageRouteAssetPreloadProvider>
  );
};

export default memo(AppPageRoutesWithTelemetry);
